/**
 * Home Safety Audit — Room Page
 * Route: /home-audit/:room
 *
 * Per-room wizard step (D-01, D-04):
 *   - Reads active session + question catalog in parallel
 *   - Buffers answers in local useState
 *   - Pre-fills from session.responses on mount (AUD-05 / Pitfall 2)
 *   - Flushes the buffer via POST /sessions/:id/responses when user clicks Next
 *   - On the last room, additionally fires POST /sessions/:id/complete then routes to /results
 *
 * If slug is unknown or no active session exists, Navigates back to /home-audit.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  useActiveSession,
  useCompleteSession,
  useQuestions,
  useSaveResponses,
} from '../hooks/useHomeAudit';
import {
  ROOM_INTROS,
  ROOM_LABELS,
  ROOM_SLUG_TO_ENUM,
  activeRoomOrder,
  isRoomSlug,
  nextRoomSlug,
  prevRoomSlug,
  roomAreaFromSlug,
} from '../utils/roomMap';
import type { AnswerValue, AuditQuestion } from '../types/homeAudit.types';
import { QuestionItem } from './QuestionItem';

type RoomAnswers = Record<string, AnswerValue>;

export const RoomPage = () => {
  const { room } = useParams<{ room: string }>();
  const navigate = useNavigate();
  const { data: session, isLoading: sessionLoading } = useActiveSession();
  const { data: questions, isLoading: questionsLoading } = useQuestions();
  const saveResponses = useSaveResponses();
  const completeSession = useCompleteSession();
  const [answers, setAnswers] = useState<RoomAnswers>({});

  // Pre-fill from server responses (Pitfall 2)
  useEffect(() => {
    if (!session || !isRoomSlug(room)) return;
    const roomArea = ROOM_SLUG_TO_ENUM[room];
    const prefill = session.responses
      .filter((r) => r.roomArea === roomArea)
      .reduce<RoomAnswers>((acc, r) => {
        acc[r.questionId] = r.answer;
        return acc;
      }, {});
    setAnswers(prefill);
  }, [session, room]);

  const roomQuestions = useMemo<AuditQuestion[]>(() => {
    if (!questions || !isRoomSlug(room)) return [];
    const area = ROOM_SLUG_TO_ENUM[room];
    return questions.filter((q) => q.roomArea === area);
  }, [questions, room]);

  if (sessionLoading || questionsLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </div>
    );
  }

  if (!session) return <Navigate to="/home-audit" replace />;
  if (!isRoomSlug(room)) return <Navigate to="/home-audit" replace />;

  const roomArea = roomAreaFromSlug(room);
  const next = nextRoomSlug(room, session.stairsNa);
  const prev = prevRoomSlug(room, session.stairsNa);
  const order = activeRoomOrder(session.stairsNa);
  const roomIndex = order.indexOf(room);
  const totalRooms = order.length;
  const isLastRoom = next === null;
  const allAnswered = roomQuestions.every((q) => !!answers[q.id]);
  const saving = saveResponses.isPending || completeSession.isPending;

  const handleAnswer = (questionId: string, answer: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = async () => {
    if (!allAnswered || saving) return;
    await saveResponses.mutateAsync({
      sessionId: session.id,
      dto: {
        roomArea,
        responses: roomQuestions.map((q) => ({
          questionId: q.id,
          answer: answers[q.id],
        })),
      },
    });
    if (isLastRoom) {
      await completeSession.mutateAsync(session.id);
      navigate(`/home-audit/results?sessionId=${session.id}`);
    } else {
      navigate(`/home-audit/${next}`);
    }
  };

  const handleBack = () => {
    if (prev) navigate(`/home-audit/${prev}`);
  };

  const nextLabel = isLastRoom
    ? saving ? 'Finishing\u2026' : 'Finish Audit'
    : saving
      ? 'Saving\u2026'
      : `Next: ${next ? ROOM_LABELS[ROOM_SLUG_TO_ENUM[next]] : ''}`;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        <div>
          <h1 className="text-3xl font-medium">{ROOM_LABELS[roomArea]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Room {roomIndex + 1} of {totalRooms}
          </p>
          <p className="mt-2 text-muted-foreground">{ROOM_INTROS[roomArea]}</p>
        </div>

        <div className="space-y-4">
          {roomQuestions.map((q) => (
            <Card key={q.id} className="p-4">
              <CardContent className="p-0">
                <QuestionItem
                  questionId={q.id}
                  text={q.text}
                  answer={answers[q.id]}
                  onAnswer={handleAnswer}
                />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 pt-4">
          {prev ? (
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              onClick={handleBack}
              disabled={saving}
              data-testid="room-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          ) : (
            <span />
          )}

          <Button
            type="button"
            className="bg-[#026857] hover:bg-[#025246] text-white h-11"
            disabled={!allAnswered || saving}
            onClick={handleNext}
            data-testid="room-next"
          >
            {nextLabel}
            {!isLastRoom && <ArrowRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {(saveResponses.isError || completeSession.isError) && (
          <p className="text-sm text-red-600">
            Your answers couldn&apos;t be saved. Check your connection and try again.
          </p>
        )}
      </div>
    </div>
  );
};
